import React, { useState, useEffect } from 'react';
import {
  ArrowRight,
  ArrowUpRight,
  Menu,
  X,
  TrendingUp,
  Users,
  ShieldCheck,
  Zap,
  Globe,
  Anchor,
  Coffee,
  ShoppingBag,
  Cpu,
  Truck,
  BarChart3,
  MapPin,
  Mail,
  CheckCircle2,
  XCircle,
  UtensilsCrossed,
  Gem,
  Sprout,
  Layers,
  Sparkles,
  DollarSign,
  Maximize2,
  Tractor
} from 'lucide-react';

// --- Styles for Custom Animations ---
const CustomStyles = () => (
  <style>{`
    @keyframes fade-in-up {
      0% { opacity: 0; transform: translateY(20px); }
      100% { opacity: 1; transform: translateY(0); }
    }
    @keyframes marquee {
      0% { transform: translateX(0); }
      100% { transform: translateX(-50%); }
    }
    .animate-fade-in-up {
      animation: fade-in-up 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    }
    .animate-marquee {
      animation: marquee 30s linear infinite;
    }
    .delay-100 { animation-delay: 0.1s; }
    .delay-200 { animation-delay: 0.2s; }
    .delay-300 { animation-delay: 0.3s; }
    
    .glass-nav {
      background: rgba(255, 255, 255, 0.9);
      backdrop-filter: blur(16px);
      -webkit-backdrop-filter: blur(16px);
    }
    
    .text-balance {
      text-wrap: balance;
    }

    /* Subtle Noise Texture for Cards */
    .bg-noise {
      background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.05'/%3E%3C/svg%3E");
    }
  `}</style>
);

// --- UI Components ---

const Button = ({ children, variant = 'primary', className = '', onClick }) => {
  const baseStyle = "inline-flex items-center justify-center gap-2 px-8 py-4 text-sm font-bold tracking-widest uppercase transition-all duration-300 rounded-full";
  const variants = {
    primary: "bg-emerald-950 text-white hover:bg-emerald-800 hover:scale-105 shadow-xl shadow-emerald-900/10",
    outline: "border border-slate-200 text-slate-900 hover:border-emerald-900 hover:bg-emerald-50",
    white: "bg-white text-emerald-950 hover:bg-emerald-50 shadow-lg",
    ghost: "text-emerald-900 hover:text-emerald-700 hover:bg-emerald-50/50"
  };

  return (
    <button onClick={onClick} className={`${baseStyle} ${variants[variant]} ${className}`}>
      {children}
    </button>
  );
};

const SectionHeader = ({ label, title, description, light = false }) => (
  <div className="max-w-3xl mb-16 md:mb-24">
    <div className={`flex items-center gap-3 mb-6 ${light ? 'text-emerald-400' : 'text-emerald-800'}`}>
      <span className="w-2 h-2 rounded-full bg-current animate-pulse"></span>
      <span className="text-xs font-bold tracking-[0.2em] uppercase">{label}</span>
    </div>
    <h2 className={`text-4xl md:text-6xl lg:text-7xl font-serif font-medium leading-[1.05] tracking-tight mb-8 ${light ? 'text-white' : 'text-slate-950'}`}>
      {title}
    </h2>
    {description && (
      <p className={`text-lg md:text-xl font-light leading-relaxed max-w-xl text-balance ${light ? 'text-slate-400' : 'text-slate-600'}`}>
        {description}
      </p>
    )}
  </div>
);

const BentoCard = ({ title, subtitle, icon: Icon, children, className = "", dark = false }) => (
  <div className={`relative group p-8 md:p-10 flex flex-col h-full transition-all duration-500 hover:-translate-y-1 ${dark ? 'bg-slate-900 text-white border-slate-800' : 'bg-white text-slate-950 border-slate-100'} rounded-[2rem] border shadow-sm hover:shadow-2xl hover:shadow-emerald-900/5 ${className}`}>
    <div className="flex justify-between items-start mb-6">
      <div className={`p-4 rounded-2xl ${dark ? 'bg-slate-800 text-emerald-400' : 'bg-emerald-50 text-emerald-800'}`}>
        <Icon size={28} strokeWidth={1.5} />
      </div>
      <ArrowUpRight className={`opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${dark ? 'text-slate-500' : 'text-slate-300'}`} />
    </div>
    <h3 className="text-2xl font-bold mb-2">{title}</h3>
    <p className={`text-xs font-bold uppercase tracking-widest mb-6 ${dark ? 'text-slate-500' : 'text-slate-400'}`}>{subtitle}</p>
    <div className={`mt-auto text-base leading-relaxed ${dark ? 'text-slate-300' : 'text-slate-600'}`}>
      {children}
    </div>
  </div>
);

// --- Sections ---

const Navigation = ({ activePage, setActivePage }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navs = [
    { id: 'focus', label: 'Who We Invest In' },
    { id: 'edge', label: 'Our Support' },
    { id: 'process', label: 'How It Works' },
    { id: 'about', label: 'Our Why' },
  ];

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'glass-nav border-b border-slate-100 py-3' : 'bg-transparent py-6 md:py-8'}`}>
        <div className="container mx-auto px-6 md:px-12 flex justify-between items-center">
          <button onClick={() => setActivePage('home')} className="flex items-center gap-3 group z-50 relative">
            <div className="w-10 h-10 bg-emerald-950 text-white rounded-xl flex items-center justify-center font-serif font-bold text-xl group-hover:bg-emerald-700 transition-colors">S</div>
            <span className={`text-lg font-bold tracking-tight ${isOpen ? 'text-slate-950' : 'text-slate-950'}`}>SRSG Ventures</span>
          </button>

          <div className="hidden md:flex items-center gap-10">
            {navs.map(n => (
              <button
                key={n.id}
                onClick={() => setActivePage(n.id)}
                className={`text-xs font-bold uppercase tracking-widest hover:text-emerald-700 transition-colors ${activePage === n.id ? 'text-emerald-800' : 'text-slate-500'}`}
              >
                {n.label}
              </button>
            ))}
            <Button onClick={() => setActivePage('contact')} className="ml-4 py-3 px-6 text-xs">
              Apply Now
            </Button>
          </div>

          <button onClick={() => setIsOpen(!isOpen)} className="md:hidden z-50 p-2 text-slate-950">
            {isOpen ? <X /> : <Menu />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div className={`fixed inset-0 bg-white z-40 transition-transform duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] ${isOpen ? 'translate-y-0' : '-translate-y-full'}`}>
        <div className="h-full flex flex-col justify-center px-8 gap-8">
          {navs.map(n => (
            <button
              key={n.id}
              onClick={() => { setActivePage(n.id); setIsOpen(false); }}
              className="text-4xl font-serif font-medium text-slate-950 text-left hover:text-emerald-700 transition-colors"
            >
              {n.label}
            </button>
          ))}
          <div className="h-px bg-slate-100 w-full my-4"></div>
          <button
            onClick={() => { setActivePage('contact'); setIsOpen(false); }}
            className="text-xl font-bold uppercase tracking-widest text-left text-emerald-800"
          >
            Apply for Funding →
          </button>
        </div>
      </div>
    </>
  );
};

const Hero = ({ setActivePage }) => (
  <header className="relative min-h-[95vh] pt-32 pb-20 flex flex-col justify-center bg-[#F8F9FA] overflow-hidden">
    {/* Background Blurs */}
    <div className="absolute top-0 right-0 w-[50vw] h-[50vw] bg-emerald-100/40 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2 mix-blend-multiply pointer-events-none"></div>
    <div className="absolute bottom-0 left-0 w-[40vw] h-[40vw] bg-blue-100/40 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2 mix-blend-multiply pointer-events-none"></div>

    <div className="container mx-auto px-6 md:px-12 relative z-10">
      <div className="max-w-5xl">
        <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-white border border-emerald-100 shadow-sm mb-12 animate-fade-in-up">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
          <span className="text-xs font-bold tracking-widest uppercase text-emerald-900">Now Deploying Fund I (₹5 Cr)</span>
        </div>

        <h1 className="text-6xl md:text-8xl lg:text-[7.5rem] font-serif font-medium leading-[0.9] text-slate-950 mb-12 tracking-tighter animate-fade-in-up delay-100">
          We bring the <span className="italic text-emerald-800">Muscle</span> <br />
          to your Brand.
        </h1>

        <div className="flex flex-col md:flex-row gap-12 md:items-end justify-between border-t border-slate-200 pt-12 animate-fade-in-up delay-200">
          <div className="max-w-lg">
            <p className="text-2xl text-slate-900 leading-snug mb-4 font-medium">
              More than just money.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed text-balance">
              SRSG Ventures bridges the gap for early-stage Food & D2C startups. We provide the <strong>capital to grow</strong> and the <strong>distribution network</strong> to sell.
            </p>
          </div>
          <div className="flex flex-wrap gap-4">
            <Button onClick={() => setActivePage('contact')}>Pitch Your Idea <ArrowRight size={18} /></Button>
            <Button variant="outline" onClick={() => setActivePage('edge')}>How We Help</Button>
          </div>
        </div>
      </div>
    </div>

    {/* Rolling Ticker */}
    <div className="w-full border-y border-slate-200 bg-white/50 py-6 mt-20 backdrop-blur-sm">
      <div className="flex gap-16 animate-marquee whitespace-nowrap text-emerald-900/40 text-xs font-bold tracking-[0.2em] uppercase justify-center items-center">
        {['Early Stage Capital', 'Pan-India Distribution', 'Operational Support', 'F&B Specialists', 'D2C Growth', 'Founder Friendly'].map((tag, i) => (
          <React.Fragment key={i}>
            <span>{tag}</span>
            <span className="w-1.5 h-1.5 bg-emerald-900/20 rounded-full"></span>
          </React.Fragment>
        ))}
        {['Early Stage Capital', 'Pan-India Distribution', 'Operational Support', 'F&B Specialists', 'D2C Growth', 'Founder Friendly'].map((tag, i) => (
          <React.Fragment key={`dup-${i}`}>
            <span>{tag}</span>
            <span className="w-1.5 h-1.5 bg-emerald-900/20 rounded-full"></span>
          </React.Fragment>
        ))}
      </div>
    </div>
  </header>
);

const About = () => (
  <section className="py-32 bg-white">
    <div className="container mx-auto px-6 md:px-12">
      <SectionHeader
        label="The Problem We Solve"
        title="The 'Missing Middle' in Indian Funding"
        description="Great products often die because they are too big for friends & family funding, but too small for big Venture Capitalists. We fix that."
      />

      <div className="grid md:grid-cols-2 gap-12 md:gap-24 mb-24">
        <div className="bg-slate-50 p-10 rounded-[2.5rem] border border-slate-100">
          <div className="flex items-center gap-3 mb-6 text-slate-400">
            <XCircle size={24} />
            <h4 className="font-bold uppercase tracking-widest text-sm">The Struggle</h4>
          </div>
          <p className="text-2xl font-serif text-slate-900 leading-relaxed mb-6">
            "I have a great product and some sales, but I can't afford a national launch. Big VCs want me to have ₹10Cr revenue first."
          </p>
          <div className="h-2 w-24 bg-red-200 rounded-full"></div>
        </div>

        <div className="bg-emerald-950 p-10 rounded-[2.5rem] text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-6 text-emerald-400">
              <CheckCircle2 size={24} />
              <h4 className="font-bold uppercase tracking-widest text-sm">The SRSG Solution</h4>
            </div>
            <p className="text-2xl font-serif text-white leading-relaxed mb-6">
              "We give you the ₹10L-₹50L you need to grow, PLUS we use our trucks and warehouses to put your product in stores."
            </p>
            <div className="h-2 w-24 bg-emerald-500 rounded-full"></div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        {[
          { icon: Anchor, title: "Patient Capital", desc: "We don't force you to grow at unhealthy speeds. We build solid, profitable businesses first." },
          { icon: TrendingUp, title: "Profit First", desc: "We focus on 'Unit Economics'—simply put, ensuring you make money on every single packet you sell." },
          { icon: Globe, title: "National Scale", desc: "We help you move from being a 'local favorite' in your city to a national brand available across India." }
        ].map((item, i) => (
          <div key={i} className="p-8 group hover:bg-slate-50 transition-colors rounded-3xl">
            <div className="w-14 h-14 bg-emerald-100/50 rounded-2xl flex items-center justify-center text-emerald-800 mb-6 group-hover:scale-110 transition-transform">
              <item.icon size={28} />
            </div>
            <h3 className="text-xl font-bold text-slate-950 mb-3">{item.title}</h3>
            <p className="text-slate-600 leading-relaxed text-sm">{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Focus = () => (
  <section className="py-32 bg-slate-950 text-white relative overflow-hidden">
    {/* Grid Pattern Overlay */}
    <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>

    <div className="container mx-auto px-6 md:px-12 relative z-10">
      <SectionHeader
        light
        label="Investment Thesis"
        title="Where & Why We Invest"
        description="We focus on sectors where we can provide a significant, unfair advantage using our established operational backbone."
      />

      <div className="grid md:grid-cols-2 gap-8">

        {/* Card 1: Primary Focus - Food & Beverage */}
        <BentoCard
          icon={UtensilsCrossed}
          title="F&B: The Indian Palate"
          subtitle="Consumer Packaged Goods (CPG)"
          dark={true}
          className="bg-emerald-900/50 border-emerald-800 hover:bg-emerald-800/70"
        >
          <ul className="space-y-3 list-disc list-inside text-slate-300">
            <li><strong>Healthy & Novel Snacking:</strong> Ready-to-eat products focused on regional flavors or health-conscious alternatives.</li>
            <li><strong>Beverages:</strong> Functional drinks, packaged juices, and innovative non-alcoholic options.</li>
            <li><strong>Clean Label:</strong> Brands emphasizing minimal ingredients, transparency, and sustainable sourcing.</li>
          </ul>
        </BentoCard>

        {/* Card 2: Primary Focus - D2C Brands */}
        <BentoCard
          icon={Gem}
          title="D2C: Digital-First Loyalty"
          subtitle="High Retention & Cult Following"
          dark={true}
          className="bg-indigo-900/50 border-indigo-800 hover:bg-indigo-800/70"
        >
          <ul className="space-y-3 list-disc list-inside text-slate-300">
            <li><strong>Personal Care & Wellness:</strong> Brands with high repeat purchase rates and strong subscriber models.</li>
            <li><strong>Home Goods & Lifestyle:</strong> Focus on unique, niche products that command premium pricing online.</li>
            <li><strong>Omnichannel Readiness:</strong> Brands ready to transition from being "Instagram famous" to having a retail presence.</li>
          </ul>
        </BentoCard>

        {/* Card 3: Our Investment Criteria & Stage */}
        <BentoCard
          icon={Sparkles}
          title="What We Look For"
          subtitle="Our Due Diligence Checklist"
          dark={true}
          className="bg-slate-800/50 border-slate-700 hover:bg-slate-700/70"
        >
          <ul className="space-y-3 list-disc list-inside text-slate-300">
            <li><strong>Stage:</strong> Early-Growth Stage (Proven Concept or Prototype with initial revenue).</li>
            <li><strong>Scalability:</strong> Clear potential for Pan-India expansion via our distribution network.</li>
            <li><strong>Unit Economics:</strong> Proven path to profitability and high gross margins.</li>
            <li><strong>Team:</strong> Highly committed and execution-focused founding teams.</li>
          </ul>
        </BentoCard>

        {/* Card 4: Deal Structure and Secondary Focus */}
        <BentoCard
          icon={DollarSign}
          title="The Deal Structure"
          subtitle="Capital + Operational Leverage"
          dark={true}
          className="bg-rose-900/50 border-rose-800 hover:bg-rose-800/70"
        >
          <ul className="space-y-3 list-disc list-inside text-slate-300">
            <li><strong>Ticket Size:</strong> We invest between <strong>₹10L to ₹50L</strong> to accelerate growth.</li>
            <li><strong>Equity:</strong> We typically seek 10-20% equity stake in the company.</li>
            <li><strong>Secondary Focus:</strong> Selective investments in Tech Enablers (Agri-Tech, Supply Chain, Retail-Tech).</li>
            <li><strong>The Value Add:</strong> Funding is always packaged with our physical distribution network.</li>
          </ul>
        </BentoCard>

      </div>
    </div>
  </section>
);

const Edge = () => (
  <section className="py-32 bg-[#F0F4F8]">
    <div className="container mx-auto px-6 md:px-12">
      <SectionHeader
        label="Our Platform"
        title="Your Extended Co-Founding Team"
        description="Most investors give advice. We give resources. We operate like a Venture Studio, plugging your brand directly into our infrastructure."
      />

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        <BentoCard icon={Truck} title="Distribution" subtitle="The Muscle">
          We get you into shops. Access our established logistics network for Modern Trade, General Trade, and Quick Commerce listings.
          <br /><br />
          <span className="font-bold text-emerald-800">Benefit:</span> Immediate sales velocity.
        </BentoCard>

        <BentoCard icon={ShieldCheck} title="Legal & IP" subtitle="The Shield">
          Founders shouldn't waste time on red tape. Our in-house team handles FSSAI licenses, Trademarking, and Compliance.
          <br /><br />
          <span className="font-bold text-emerald-800">Benefit:</span> Zero regulatory headaches.
        </BentoCard>

        <BentoCard icon={BarChart3} title="Finance" subtitle="The Brain">
          Our CAs help you build professional financial models, manage cash flow, and prepare for your next big funding round.
          <br /><br />
          <span className="font-bold text-emerald-800">Benefit:</span> Audit-ready books.
        </BentoCard>

        <BentoCard icon={Users} title="Mentorship" subtitle="The Guide">
          Regular sessions with veterans who have built brands before. No theoretical advice—just tactical "how-to" guidance.
          <br /><br />
          <span className="font-bold text-emerald-800">Benefit:</span> Fewer rookie mistakes.
        </BentoCard>
      </div>
    </div>
  </section>
);

const Process = () => (
  <section className="py-32 bg-white">
    <div className="container mx-auto px-6 md:px-12">
      <div className="flex flex-col md:flex-row gap-16 md:gap-32 items-start">
        <div className="md:w-1/3 md:sticky md:top-32">
          <SectionHeader
            label="The Timeline"
            title="A Simple, Transparent Process"
            description="We respect your time. Our process is designed to get to a 'Yes' or a helpful 'No' quickly so you can get back to building."
          />
          <Button onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}>Start Application</Button>
        </div>

        <div className="md:w-2/3 border-l-2 border-slate-100 pl-8 md:pl-16 space-y-20">
          {[
            { step: "01", title: "Submit Application", desc: "Fill out our simple form. We don't need a 50-page deck. Just show us your product, your current sales, and your team." },
            { step: "02", title: "The 'Taste' Test", desc: "If we like the numbers, we meet. We taste your product, visit your office, and understand your vision." },
            { step: "03", title: "The Offer", desc: "If it's a fit, we give you a Term Sheet. Standard, clean terms. We typically take 10-20% equity for capital + support." },
            { step: "04", title: "The 90-Day Sprint", desc: "Day 1 is not the end; it's the start. We begin a 90-day intensive sprint to fix operations and launch in new stores." }
          ].map((item, i) => (
            <div key={i} className="relative group">
              <span className="absolute -left-[calc(2rem+9px)] md:-left-[calc(4rem+9px)] top-0 w-4 h-4 bg-white border-4 border-slate-200 rounded-full group-hover:border-emerald-500 transition-colors"></span>
              <span className="text-6xl md:text-8xl font-serif text-slate-100 absolute -top-8 -left-4 -z-10 group-hover:text-emerald-50 transition-colors">{item.step}</span>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-950 mb-4">{item.title}</h3>
              <p className="text-lg text-slate-600 leading-relaxed max-w-lg">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Contact = () => (
  <section id="contact" className="py-32 bg-slate-950 text-white relative">
    <div className="container mx-auto px-6 md:px-12">
      <div className="max-w-5xl mx-auto bg-slate-900 rounded-[3rem] p-8 md:p-16 border border-slate-800 shadow-2xl relative overflow-hidden">
        {/* Background glow */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="text-center mb-16 relative z-10">
          <h2 className="text-4xl md:text-6xl font-serif mb-6">Partner with SRSG</h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Tell us about your product. If it's a fit, we move fast. We reply to every application within 7 days.
          </p>
        </div>

        <form className="space-y-6 relative z-10" onSubmit={e => e.preventDefault()}>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500 ml-4">Full Name</label>
              <input className="w-full bg-slate-950 border border-slate-800 rounded-full px-8 py-4 focus:border-emerald-500 focus:outline-none transition-colors text-white" placeholder="Jane Doe" />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500 ml-4">Startup Name</label>
              <input className="w-full bg-slate-950 border border-slate-800 rounded-full px-8 py-4 focus:border-emerald-500 focus:outline-none transition-colors text-white" placeholder="Venture X" />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500 ml-4">Email Address</label>
            <input className="w-full bg-slate-950 border border-slate-800 rounded-full px-8 py-4 focus:border-emerald-500 focus:outline-none transition-colors text-white" placeholder="founder@venturex.com" />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500 ml-4">Your Pitch</label>
            <textarea className="w-full bg-slate-950 border border-slate-800 rounded-[2rem] px-8 py-6 focus:border-emerald-500 focus:outline-none transition-colors min-h-[160px] text-white" placeholder="What problem are you solving? What is your monthly revenue?" />
          </div>

          <div className="pt-6 text-center">
            <Button className="w-full md:w-auto min-w-[200px]">Submit Application</Button>
          </div>
          <p className="text-center text-slate-600 text-sm mt-6">We respect your IP. All submissions are confidential.</p>
        </form>
      </div>

      <div className="flex flex-col md:flex-row justify-between items-center gap-8 mt-24 pt-12 border-t border-slate-900 text-slate-500 text-sm font-medium uppercase tracking-widest">
        <div className="flex items-center gap-2">
          <MapPin size={16} /> New Delhi, India
        </div>
        <div className="flex gap-8">
          <a href="#" className="hover:text-emerald-500 transition-colors flex items-center gap-2">LinkedIn</a>
          <a href="#" className="hover:text-emerald-500 transition-colors flex items-center gap-2">Twitter</a>
          <a href="mailto:pitch@srsgventures.com" className="hover:text-emerald-500 transition-colors flex items-center gap-2">pitch@srsgventures.com</a>
        </div>
      </div>
    </div>
  </section>
);

const App = () => {
  const [activePage, setActivePage] = useState('home');

  // Simple router logic
  const renderPage = () => {
    switch (activePage) {
      case 'home': return (
        <>
          <Hero setActivePage={setActivePage} />
          <About />
          <Focus />
          <Edge />
          <Process />
          <Contact />
        </>
      );
      case 'focus': return <div className="pt-20"><Focus /><div className="bg-slate-950 pb-20 text-center"><Button variant="white" onClick={() => setActivePage('contact')}>Apply Now</Button></div></div>;
      case 'edge': return <div className="pt-20"><Edge /><div className="bg-[#F0F4F8] pb-20 text-center"><Button onClick={() => setActivePage('contact')}>Apply Now</Button></div></div>;
      case 'process': return <div className="pt-20"><Process /><div className="bg-white pb-20 text-center"><Button onClick={() => setActivePage('contact')}>Apply Now</Button></div></div>;
      case 'about': return <div className="pt-20"><About /><div className="bg-white pb-20 text-center"><Button onClick={() => setActivePage('contact')}>Apply Now</Button></div></div>;
      case 'contact': return <div className="pt-20"><Contact /></div>;
      default: return null;
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activePage]);

  return (
    <div className="font-sans text-slate-950 bg-white selection:bg-emerald-200 selection:text-emerald-950">
      <CustomStyles />
      <Navigation activePage={activePage} setActivePage={setActivePage} />
      <main>
        {renderPage()}
      </main>
    </div>
  );
};

export default App;